import React, { useCallback } from 'react';
import styled from 'styled-components';

const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  height: 36px;
  display: flex;
  align-items: center;
`;

const SliderTrack = styled.div`
  position: absolute;
  width: 100%;
  height: 6px;
  background: #444;
  border-radius: 3px;
`;

const SliderRange = styled.div<{ $left: number; $width: number }>`
  position: absolute;
  height: 6px;
  background: #007bff;
  border-radius: 3px;
  left: ${props => props.$left}%;
  width: ${props => props.$width}%;
`;

const ThumbInput = styled.input`
  position: absolute;
  width: 100%;
  height: 36px;
  margin: 0;
  background: none;
  pointer-events: none;
  -webkit-appearance: none;
  appearance: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    pointer-events: auto;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #007bff;
    border: 2px solid #ffffff;
    cursor: pointer;
  }

  &::-moz-range-thumb {
    pointer-events: auto;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #007bff;
    border: 2px solid #ffffff;
    cursor: pointer;
  }
`;

interface DualRangeSliderProps {
  min: number;
  max: number;
  step: number;
  minValue: number;
  maxValue: number;
  onChange: (minValue: number, maxValue: number) => void;
}

/**
 * Two-thumb range slider. The two native range inputs are stacked; thumbs are
 * individually clickable via pointer-events.
 */
const DualRangeSlider: React.FC<DualRangeSliderProps> = ({ min, max, step, minValue, maxValue, onChange }) => {
  const toPercent = useCallback((value: number) => ((value - min) / (max - min)) * 100, [min, max]);

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.min(parseFloat(e.target.value), maxValue - step);
    onChange(value, maxValue);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.max(parseFloat(e.target.value), minValue + step);
    onChange(minValue, value);
  };

  return (
    <SliderContainer>
      <SliderTrack />
      <SliderRange $left={toPercent(minValue)} $width={toPercent(maxValue) - toPercent(minValue)} />
      <ThumbInput
        type="range"
        min={min}
        max={max}
        step={step}
        value={minValue}
        onChange={handleMinChange}
        style={{ zIndex: minValue > max - (max - min) / 10 ? 5 : 3 }}
      />
      <ThumbInput
        type="range"
        min={min}
        max={max}
        step={step}
        value={maxValue}
        onChange={handleMaxChange}
        style={{ zIndex: 4 }}
      />
    </SliderContainer>
  );
};

export default DualRangeSlider;
